/**
 * PATH: packages/bills/src/components/defaultBillSelection.ts
 * FILE_ID: BILLS-PKG-CMP-DEFAULTSEL-001
 * PURPOSE: Decide which bill (if any) the workspace should auto-open on first
 *          load, so an operator lands on the latest bill instead of an empty
 *          "Select a bill to preview…" pane.
 * WHY: Operator ask 2026-06-22 — "When opening bills by default it should open
 *      to the latest one." The list is returned newest-first, so the latest
 *      bill is simply the first row (`bills[0]`).
 *
 * FLOW:
 *   bills[] + {initialBillId, selectedId} ─▶ id to auto-select | null
 *
 * RELATED:
 *   - BookkeepingWorkspace.tsx: calls this once after the list resolves.
 */

import type { BillListRow } from '../types';

/**
 * f(bills, {initialBillId, selectedId}) → the bill id to auto-select, or null
 * when nothing should change.
 *
 * Returns null (no auto-select) when:
 *   - a deep-link (`initialBillId`) is present — the URL already chose a bill;
 *   - a bill is already selected — never override the operator's choice;
 *   - the list is empty / not yet loaded.
 * Otherwise returns the first (newest) row's id.
 */
export function pickDefaultBillId(
  bills: Pick<BillListRow, 'id'>[] | undefined | null,
  opts: { initialBillId: string | null; selectedId: string | null },
): string | null {
  if (opts.initialBillId) return null;
  if (opts.selectedId) return null;
  const first = bills && bills.length > 0 ? bills[0] : null;
  return first ? first.id : null;
}
